/**
 * GraphQL Type Compatibility Layer
 *
 * This module provides type aliases and re-exports to bridge the gap between
 * frontend component expectations and the actual generated GraphQL types.
 *
 * WHY THIS EXISTS:
 * - Backend schema evolved, some enum/type names changed
 * - Frontend components expect certain names (e.g., SafetyLevel, WorkflowStatus)
 * - Generated types have different names (e.g., FormSafetyLevel, WorkflowState)
 *
 * USAGE:
 * Instead of: import { SafetyLevel } from '@/generated/graphql'
 * Use: import { SafetyLevel } from '@/types/graphql-compat'
 */

// Re-export all generated types
export * from '../generated/graphql';

// Import specific types we need to alias
import {
  FormSafetyLevel,
  TaskStatus as GeneratedTaskStatus,
  ActorStatus as GeneratedActorStatus,
  GqlActorStatus,
  GqlTaskStatus,
  GqlTaskPriority,
  GqlConstraintType,
  GqlDependencyType,
  GqlStateType,
  GqlContractStatus,
  GqlScheduleStatus,
  GqlComplianceStatus,
  RiskLevelOutput,
  EvidenceType as GeneratedEvidenceType,
  // Import types for extension
  type Actor as GeneratedActor,
  type Policy as GeneratedPolicy,
  type Queue as GeneratedQueue,
  type Schedule as GeneratedSchedule,
  type Permission as GeneratedPermission,
} from '../generated/graphql';

// ============================================================================
// SAFETY LEVEL COMPATIBILITY
// ============================================================================

/**
 * SafetyLevel - Extended enum with DRR-027 compliant values
 *
 * Maps the 5-level safety system (LIFE_CRITICAL, CRITICAL, HIGH, MEDIUM, LOW)
 * to the backend's FormSafetyLevel enum (LIFE_CRITICAL, CRITICAL, ELEVATED, STANDARD).
 *
 * Used by: Task, Workflow, Policy, all NormalizedEntity components
 */
export const SafetyLevel = {
  // Direct mappings to FormSafetyLevel
  LIFE_CRITICAL: FormSafetyLevel.LIFE_CRITICAL,
  CRITICAL: FormSafetyLevel.CRITICAL,
  // Extended mappings for 5-level system
  HIGH: FormSafetyLevel.ELEVATED,
  MEDIUM: FormSafetyLevel.STANDARD,
  LOW: FormSafetyLevel.STANDARD, // Maps to STANDARD (lowest risk)
  // Also expose original values for compatibility
  ELEVATED: FormSafetyLevel.ELEVATED,
  STANDARD: FormSafetyLevel.STANDARD,
} as const;

export type SafetyLevel = (typeof SafetyLevel)[keyof typeof SafetyLevel];

// ============================================================================
// TASK STATUS COMPATIBILITY
// ============================================================================

/**
 * Normalized TaskStatus values
 * The generated enum has mixed casing - this normalizes to SCREAMING_SNAKE_CASE
 */
export const TaskStatusNormalized = {
  IN_PROGRESS: GeneratedTaskStatus.IN_PROGRESS,
  UNKNOWN: GeneratedTaskStatus.UNKNOWN,
  BLOCKED: GeneratedTaskStatus.blocked,
  DONE: GeneratedTaskStatus.done,
  EXPIRED: GeneratedTaskStatus.expired,
  OPEN: GeneratedTaskStatus.open,
  // Aliases for frontend expectations (CRITICAL: These are the names components use)
  InProgress: GeneratedTaskStatus.IN_PROGRESS,
  Failed: GeneratedTaskStatus.blocked,
  Completed: GeneratedTaskStatus.done,
  Pending: GeneratedTaskStatus.open,
  // Additional aliases matching common usage patterns
  COMPLETED: GeneratedTaskStatus.done,
  PENDING: GeneratedTaskStatus.open,
  CANCELLED: GeneratedTaskStatus.blocked, // No direct match, map to blocked
  FAILED: GeneratedTaskStatus.blocked,
} as const;

/**
 * TaskStatusCompat - Use this instead of TaskStatus enum directly
 * Provides all expected status values with correct backend mappings
 */
export const TaskStatusCompat = TaskStatusNormalized;

// ============================================================================
// ACTOR STATUS COMPATIBILITY
// ============================================================================

/**
 * Actor status compatibility - add missing values
 */
export const ActorStatusCompat = {
  ...GeneratedActorStatus,
  // Add expected values that may not exist in generated types
  ACTIVE: 'ACTIVE' as const,
  INACTIVE: 'INACTIVE' as const,
  PENDING: 'PENDING' as const,
  SUSPENDED: 'SUSPENDED' as const,
};

// ============================================================================
// TYPE EXTENSIONS
// ============================================================================

/**
 * Extended Actor type with expected fields
 */
export interface ActorExtended extends GeneratedActor {
  email?: string;
  roles?: string[];
}

/**
 * Extended Policy type with expected fields
 */
export interface PolicyExtended extends GeneratedPolicy {
  priority?: number;
  status?: string;
}

/**
 * Extended Queue type with expected fields
 */
export interface QueueExtended extends GeneratedQueue {
  name?: string;
  priority?: number;
  maxSize?: number;
  status?: string;
}

/**
 * Extended Schedule type with expected fields
 */
export interface ScheduleExtended extends GeneratedSchedule {
  name?: string;
  description?: string;
  cron?: string;
  enabled?: boolean;
}

/**
 * Extended Permission type with expected fields
 */
export interface PermissionExtended extends GeneratedPermission {
  actorId?: string;
  resource?: string;
  actions?: string[];
  conditions?: Record<string, unknown>;
}

// ============================================================================
// WORKFLOW STATUS COMPATIBILITY
// ============================================================================

/**
 * WorkflowStatus - alias for component expectations
 * Note: Generated types may use WorkflowState
 */
export const WorkflowStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const;

export type WorkflowStatus = (typeof WorkflowStatus)[keyof typeof WorkflowStatus];

// ============================================================================
// CONSTRAINT TYPE COMPATIBILITY
// ============================================================================

/**
 * ConstraintSeverity - may need to be defined if not in generated types
 */
export const ConstraintSeverity = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL',
} as const;

export type ConstraintSeverity = (typeof ConstraintSeverity)[keyof typeof ConstraintSeverity];

// ============================================================================
// POLICY/QUEUE STATUS COMPATIBILITY
// ============================================================================

/**
 * PolicyStatus - common status values for policies
 */
export const PolicyStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  DRAFT: 'DRAFT',
  ARCHIVED: 'ARCHIVED',
  DEPRECATED: 'DEPRECATED',
} as const;

export type PolicyStatus = (typeof PolicyStatus)[keyof typeof PolicyStatus];

/**
 * QueueStatus - common status values for queues
 */
export const QueueStatus = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  DRAINING: 'DRAINING',
  STOPPED: 'STOPPED',
  EMPTY: 'EMPTY',
} as const;

export type QueueStatus = (typeof QueueStatus)[keyof typeof QueueStatus];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a value is a valid SafetyLevel
 */
export function isValidSafetyLevel(value: unknown): value is SafetyLevel {
  return Object.values(FormSafetyLevel).includes(value as FormSafetyLevel);
}

/**
 * Get display name for TaskStatus
 */
export function getTaskStatusDisplayName(status: GeneratedTaskStatus): string {
  const displayNames: Record<GeneratedTaskStatus, string> = {
    [GeneratedTaskStatus.IN_PROGRESS]: 'In Progress',
    [GeneratedTaskStatus.UNKNOWN]: 'Unknown',
    [GeneratedTaskStatus.blocked]: 'Blocked',
    [GeneratedTaskStatus.done]: 'Done',
    [GeneratedTaskStatus.expired]: 'Expired',
    [GeneratedTaskStatus.open]: 'Open',
  };
  return displayNames[status] || status;
}

// ============================================================================
// TASK STATUS ENUM (For components expecting TaskStatus with specific values)
// ============================================================================

/**
 * TaskStatus - normalized enum for component usage
 * Maps to GqlTaskStatus values
 */
export const TaskStatus = {
  PENDING: GqlTaskStatus.PENDING,
  ASSIGNED: GqlTaskStatus.ASSIGNED,
  IN_PROGRESS: GqlTaskStatus.IN_PROGRESS,
  AWAITING_APPROVAL: GqlTaskStatus.PENDING_EXTERNAL,
  BLOCKED: GqlTaskStatus.BLOCKED,
  COMPLETED: GqlTaskStatus.COMPLETED,
  CANCELLED: GqlTaskStatus.CANCELLED,
  FAILED: GqlTaskStatus.FAILED,
  // Legacy mappings
  InProgress: GqlTaskStatus.IN_PROGRESS,
  Failed: GqlTaskStatus.FAILED,
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

// ============================================================================
// TASK PRIORITY COMPATIBILITY
// ============================================================================

/**
 * TaskPriority - maps to GqlTaskPriority
 */
export const TaskPriority = {
  LOW: GqlTaskPriority.LOW,
  NORMAL: GqlTaskPriority.NORMAL,
  HIGH: GqlTaskPriority.HIGH,
  CRITICAL: GqlTaskPriority.CRITICAL,
  // Add URGENT as alias for CRITICAL
  URGENT: GqlTaskPriority.CRITICAL,
} as const;

export type TaskPriority = (typeof TaskPriority)[keyof typeof TaskPriority];

// ============================================================================
// ACTOR STATUS COMPATIBILITY (Enhanced)
// ============================================================================

/**
 * ActorStatus - maps to GqlActorStatus
 */
export const ActorStatus = {
  ACTIVE: GqlActorStatus.ACTIVE,
  INACTIVE: GqlActorStatus.SUSPENDED, // Map inactive to suspended
  PENDING: GqlActorStatus.IDLE, // Map pending to idle
  SUSPENDED: GqlActorStatus.SUSPENDED,
  BUSY: GqlActorStatus.BUSY,
  FAILED: GqlActorStatus.FAILED,
  TERMINATED: GqlActorStatus.TERMINATED,
  IDLE: GqlActorStatus.IDLE,
} as const;

export type ActorStatus = (typeof ActorStatus)[keyof typeof ActorStatus];

// ============================================================================
// CONSTRAINT ENUMS COMPATIBILITY
// ============================================================================

/**
 * ConstraintType - maps to GqlConstraintType with frontend-friendly names
 */
export const ConstraintType = {
  VALIDATION: GqlConstraintType.BUSINESS_RULE,
  BUSINESS: GqlConstraintType.BUSINESS_RULE,
  TIMING: GqlConstraintType.TEMPORAL,
  RESOURCE: GqlConstraintType.RESOURCE,
  AUTHORIZATION: GqlConstraintType.REGULATORY,
  // Direct mappings
  BUSINESS_RULE: GqlConstraintType.BUSINESS_RULE,
  CAPACITY: GqlConstraintType.CAPACITY,
  CUSTOM: GqlConstraintType.CUSTOM,
  FINANCIAL: GqlConstraintType.FINANCIAL,
  QUOTA: GqlConstraintType.QUOTA,
  RATE_LIMIT: GqlConstraintType.RATE_LIMIT,
  REGULATORY: GqlConstraintType.REGULATORY,
  TEMPORAL: GqlConstraintType.TEMPORAL,
} as const;

export type ConstraintType = (typeof ConstraintType)[keyof typeof ConstraintType];

// ============================================================================
// DEPENDENCY TYPE COMPATIBILITY
// ============================================================================

/**
 * DependencyType - maps to GqlDependencyType with frontend-friendly names
 */
export const DependencyType = {
  REQUIRES: GqlDependencyType.FINISH_TO_START,
  BLOCKS: GqlDependencyType.START_TO_FINISH,
  TRIGGERS: GqlDependencyType.FINISH_TO_START,
  OPTIONAL: GqlDependencyType.FINISH_TO_FINISH,
  // Direct mappings
  FINISH_TO_START: GqlDependencyType.FINISH_TO_START,
  FINISH_TO_FINISH: GqlDependencyType.FINISH_TO_FINISH,
  START_TO_START: GqlDependencyType.START_TO_START,
  START_TO_FINISH: GqlDependencyType.START_TO_FINISH,
} as const;

export type DependencyType = (typeof DependencyType)[keyof typeof DependencyType];

// ============================================================================
// STATE TYPE COMPATIBILITY
// ============================================================================

/**
 * StateType - maps to GqlStateType
 */
export const StateType = {
  INITIAL: GqlStateType.INITIAL,
  INTERMEDIATE: GqlStateType.NORMAL,
  FINAL: GqlStateType.FINAL,
  ERROR: GqlStateType.FINAL, // Map error to final
  // Direct mappings
  NORMAL: GqlStateType.NORMAL,
  CHOICE: GqlStateType.CHOICE,
  PARALLEL: GqlStateType.PARALLEL,
} as const;

export type StateType = (typeof StateType)[keyof typeof StateType];

// ============================================================================
// DAGNODE TYPE COMPATIBILITY
// ============================================================================

/**
 * DagNodeType - common DAG node types
 */
export const DagNodeType = {
  TASK: 'TASK',
  GATEWAY: 'GATEWAY',
  EVENT: 'EVENT',
  TIMER: 'TIMER',
  SUBPROCESS: 'SUBPROCESS',
} as const;

export type DagNodeType = (typeof DagNodeType)[keyof typeof DagNodeType];

// ============================================================================
// CONTRACT STATUS COMPATIBILITY
// ============================================================================

/**
 * ContractStatus - maps to GqlContractStatus
 */
export const ContractStatus = {
  DRAFT: 'DRAFT' as const,
  PENDING_APPROVAL: GqlContractStatus.PAUSED,
  ACTIVE: GqlContractStatus.ACTIVE,
  SUSPENDED: GqlContractStatus.PAUSED,
  EXPIRED: GqlContractStatus.ARCHIVED,
  TERMINATED: GqlContractStatus.ARCHIVED,
  // Direct mappings
  PAUSED: GqlContractStatus.PAUSED,
  ARCHIVED: GqlContractStatus.ARCHIVED,
  BREACHED: GqlContractStatus.BREACHED,
  MET: GqlContractStatus.MET,
  WARNING: GqlContractStatus.WARNING,
} as const;

export type ContractStatus = (typeof ContractStatus)[keyof typeof ContractStatus];

// ============================================================================
// COMPLIANCE STATUS COMPATIBILITY
// ============================================================================

/**
 * ComplianceStatus - maps to GqlComplianceStatus
 */
export const ComplianceStatus = {
  PENDING_REVIEW: GqlComplianceStatus.PENDING_REVIEW,
  COMPLIANT: GqlComplianceStatus.COMPLIANT,
  PARTIAL: GqlComplianceStatus.PENDING_REVIEW,
  NON_COMPLIANT: GqlComplianceStatus.NON_COMPLIANT,
  NOT_APPLICABLE: GqlComplianceStatus.COMPLIANT,
  // Direct mappings
  EXPIRED: GqlComplianceStatus.EXPIRED,
  // Legacy aliases (map to closest new values)
  APPROVED: GqlComplianceStatus.COMPLIANT,
  UNDER_REVIEW: GqlComplianceStatus.PENDING_REVIEW,
  VIOLATIONS: GqlComplianceStatus.NON_COMPLIANT,
} as const;

export type ComplianceStatus = (typeof ComplianceStatus)[keyof typeof ComplianceStatus];

// ============================================================================
// RISK ENUMS COMPATIBILITY
// ============================================================================

/**
 * RiskSeverity - maps to RiskLevelOutput
 */
export const RiskSeverity = {
  LOW: RiskLevelOutput.LOW,
  MEDIUM: RiskLevelOutput.MEDIUM,
  HIGH: RiskLevelOutput.HIGH,
  CRITICAL: RiskLevelOutput.CRITICAL,
} as const;

export type RiskSeverity = (typeof RiskSeverity)[keyof typeof RiskSeverity];

/**
 * RiskLikelihood - likelihood values
 */
export const RiskLikelihood = {
  RARE: 'RARE',
  UNLIKELY: 'UNLIKELY',
  POSSIBLE: 'POSSIBLE',
  LIKELY: 'LIKELY',
  ALMOST_CERTAIN: 'ALMOST_CERTAIN',
} as const;

export type RiskLikelihood = (typeof RiskLikelihood)[keyof typeof RiskLikelihood];

/**
 * RiskStatus - risk status values
 */
export const RiskStatus = {
  IDENTIFIED: 'IDENTIFIED',
  ASSESSING: 'ASSESSING',
  MITIGATING: 'MITIGATING',
  ACCEPTED: 'ACCEPTED',
  CLOSED: 'CLOSED',
} as const;

export type RiskStatus = (typeof RiskStatus)[keyof typeof RiskStatus];

// ============================================================================
// EVIDENCE TYPE COMPATIBILITY
// ============================================================================

/**
 * EvidenceType - maps to broader set of evidence types
 */
export const EvidenceType = {
  DOCUMENT: 'DOCUMENT',
  LOG: 'LOG',
  SCREENSHOT: 'SCREENSHOT',
  ATTESTATION: 'ATTESTATION',
  REPORT: 'REPORT',
  CONFIGURATION: 'CONFIGURATION',
  // Generated types mapping
  bottleneck: GeneratedEvidenceType.bottleneck,
  complianceGap: GeneratedEvidenceType.complianceGap,
  errorPattern: GeneratedEvidenceType.errorPattern,
  performanceMetric: GeneratedEvidenceType.performanceMetric,
} as const;

export type EvidenceType = (typeof EvidenceType)[keyof typeof EvidenceType];

// ============================================================================
// DEPRECATED STATUS COMPATIBILITY (remove after migration)
// ============================================================================

/**
 * PolicyStatus - now defined at const object level
 * @deprecated Use PolicyStatus const instead
 */
export const PolicyStatusDeprecated = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  DRAFT: 'DRAFT',
  DEPRECATED: 'DEPRECATED',
  ARCHIVED: 'ARCHIVED',
} as const;
